import { DocumentationRequiredType, DocumentStatus, patchSetDocumentStatus } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ConfirmationCheckbox } from '@/components/modules/DocumentsValidation/components/_shared/ConfirmationCheckbox';
import { Section } from '@/components/modules/DocumentsValidation/components/_shared/Section';
import { ValidationButtons } from '@/components/modules/DocumentsValidation/components/_shared/ValidationButtons';
import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { DocumentationRequiredMap } from '@/components/modules/DocumentsValidation/maps/documentationRequired.map';
import { ValidationDeclaredDataMap } from '@/components/modules/DocumentsValidation/maps/validationDeclaredData.map';
import { ValidationSchema } from '@/components/modules/DocumentsValidation/schemas/validation.schema';
import { ValidationStep } from '@/components/modules/DocumentsValidation/types/ValidationStep';
import { updateBorrowerDocumentationRequired } from '@/components/modules/DocumentsValidation/utils/updateBorrowerDocumentationRequired';
import { DocumentIdImages } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/DocumentIdImages';
import { useErrorBoundary } from '@/components/shared/components/ErrorBoundary/hooks/useErrorBoundary';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';

interface DocumentIdValidationProps {
  onNext: () => void;
}

export const DocumentIdValidation = (props: DocumentIdValidationProps) => {
  return (
    <FormWrapper formSchema={ValidationSchema} defaultValues={{ confirmation: false }}>
      <DocumentIdForm {...props} />
    </FormWrapper>
  );
};

const DocumentIdForm = ({ onNext }: DocumentIdValidationProps) => {
  const { loan, borrowerOnboardingDetails, borrowerDocumentationRequired, update } = useDocumentsValidationContext();

  const { throwErrorInAlert } = useErrorBoundary();
  const { handleSubmit } = useFormContext();
  const [loading, setLoading] = useState(true);

  const { documentType } = ValidationDeclaredDataMap.toDocumentIdData(borrowerOnboardingDetails);

  if (!documentType) {
    throw new Error('Not able to find document type');
  }

  const onSubmit = async (validate: boolean) => {
    const files = DocumentationRequiredMap.getFilesByStep(
      borrowerDocumentationRequired,
      ValidationStep.DOCUMENT_ID_VALIDATION,
      documentType,
    );

    if (!files) {
      onNext();
      return;
    }

    const documentationRequiredId = borrowerDocumentationRequired.id;
    const unvalidatedFiles = files.filter((file) => file.status !== DocumentStatus.VALIDATED);

    try {
      setLoading(true);
      await Promise.all(
        unvalidatedFiles.map((file) => {
          return patchSetDocumentStatus({
            id: documentationRequiredId,
            fileTypeId: file.id,
            documentationRequiredType: DocumentationRequiredType.BORROWER,
            documentStatus: validate ? DocumentStatus.VALIDATED : DocumentStatus.INCIDENCE,
          });
        }),
      );
      updateBorrowerDocumentationRequired({
        borrowerDocumentationRequired,
        filesToUpdate: unvalidatedFiles,
        validate,
        update,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }

    onNext();
  };

  return (
    <Section.Content>
      <Section.Header title={`DOCUMENTS_VALIDATION_STEP_${ValidationStep.DOCUMENT_ID_VALIDATION}`} />
      <FlexColumn gridGap={8} width="100%">
        <Font variant="subtitle2" color="textSecondary">
          <Text tId={'DOCUMENT'} />
        </Font>
        <DocumentIdImages
          loanId={loan.id}
          borrowerId={loan.borrowerId}
          documentType={documentType}
          onLoaded={() => {
            setLoading(false);
          }}
          onError={(revalidate) => {
            throwErrorInAlert('ID_VALIDATION_ERROR', revalidate);
          }}
        />
      </FlexColumn>
      <ConfirmationCheckbox label={'ID_VALIDATION_CHECKBOX'} fieldVariant={FieldVariant.form} />
      <Section.Footer>
        <ValidationButtons
          onValidate={handleSubmit(() => onSubmit(true))}
          onDoNotValidate={() => onSubmit(false)}
          disabled={loading}
        />
      </Section.Footer>
    </Section.Content>
  );
};
